import { App } from './app.js';
import { renderAppShell } from './appShell.js';
import {
  ApiClient,
  loadRuntimeConfig,
  NoopTokenProvider,
  OpsHubTokenProvider,
  type AccessTokenProvider,
  type FrontendRuntimeConfig,
} from './runtime.js';
import type { ModuleShellContext } from './types.js';

const MODULE_ELEMENT_TAG = 'cio-oncall-counts-module';

class EmbeddedModuleElement extends HTMLElement {
  private readonly shadow = this.attachShadow({ mode: 'open' });
  private readonly mountEl = document.createElement('div');
  private readonly configUrl = new URL(/* @vite-ignore */ '../app-config.json', import.meta.url);
  private readonly stylesUrl = new URL(/* @vite-ignore */ '../styles.css', import.meta.url);
  private context: ModuleShellContext | null = null;
  private tokenProvider: AccessTokenProvider | null = null;
  private config: FrontendRuntimeConfig | null = null;
  private initialized = false;
  private initPromise: Promise<void> | null = null;

  set moduleContext(value: ModuleShellContext | null) {
    this.context = value;
    this.tokenProvider?.setContext?.(value);
    void this.tryInitialize();
  }

  connectedCallback(): void {
    if (!this.shadow.querySelector('link')) {
      const stylesheet = document.createElement('link');
      stylesheet.rel = 'stylesheet';
      stylesheet.href = this.stylesUrl.toString();
      this.shadow.appendChild(stylesheet);
      this.shadow.appendChild(this.mountEl);
    }

    this.addEventListener('que-context', this.handleContextUpdate as EventListener);
    void this.tryInitialize();
  }

  disconnectedCallback(): void {
    this.removeEventListener('que-context', this.handleContextUpdate as EventListener);
  }

  private handleContextUpdate = (event: CustomEvent<ModuleShellContext>): void => {
    this.moduleContext = event.detail;
  };

  private async tryInitialize(): Promise<void> {
    if (this.initialized) {
      return;
    }
    if (!this.initPromise) {
      this.initPromise = this.initialize().finally(() => {
        this.initPromise = null;
      });
    }
    return this.initPromise;
  }

  private async initialize(): Promise<void> {
    if (!this.config) {
      this.config = await loadRuntimeConfig(this.configUrl);
    }
    if (this.config.authMode === 'opshub' && !this.context) {
      return;
    }
    if (this.initialized) {
      return;
    }

    renderAppShell(this.mountEl);
    this.tokenProvider = this.createTokenProvider(this.config);
    this.tokenProvider.setContext?.(this.context);

    const app = new App(
      this.shadow,
      new ApiClient(this.config.apiBaseUrl, this.tokenProvider),
    );
    await app.init();
    this.initialized = true;
  }

  private createTokenProvider(config: FrontendRuntimeConfig): AccessTokenProvider {
    if (config.authMode === 'opshub') {
      return new OpsHubTokenProvider(config.opshubBaseUrl ?? '', config.moduleKey);
    }
    return new NoopTokenProvider();
  }
}

async function bootstrapStandalone(): Promise<void> {
  const mountEl = document.getElementById('cio-oncall-counts-standalone-root');
  if (!mountEl) return;

  const config = await loadRuntimeConfig('/app-config.json');
  renderAppShell(mountEl);

  const tokenProvider =
    config.authMode === 'opshub'
      ? new OpsHubTokenProvider(config.opshubBaseUrl ?? '', config.moduleKey)
      : new NoopTokenProvider();

  const app = new App(mountEl, new ApiClient(config.apiBaseUrl, tokenProvider));
  await app.init();
}

if (!customElements.get(MODULE_ELEMENT_TAG)) {
  customElements.define(MODULE_ELEMENT_TAG, EmbeddedModuleElement);
}

void bootstrapStandalone();
